import { Form, Row, Col, Typography, Flex } from "antd";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import CustomDatePicker from "../../../components/CustomDatePicker/CustomDatePicker";
import { useDispatch, useSelector } from "react-redux";
import DealDetail from "../../../components/Details/DealDetail";
import { GLOBAL_CONSTANTS, ERROR_MSGS, BTN_TXT } from "../../../constants/constants";
import { capitalizeWords, formatCurrency, disputeAgeCalculation, reasonRules } from "../../../helpers/helpers";
import { formatPaymentDateTime } from "../helpers";
import { getAllDisputePriority, getAllDisputeType, getDisputeRaisedFor, getAllDisputeReasonById, addDispute, getTxnDetails } from "../../../redux/slices/PaymentsSlice";
import { useEffect } from "react";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";
import { CommentsRules } from "../../../helpers/helpers";
import FormFooter from "../../../components/CustomForms/FormFooter";
import dayjs from "dayjs";

const { Text } = Typography

const RaiseDisputeForm = ({ onClose, data }) => {
    const [form] = Form.useForm();
    const disputeReasonValue = Form.useWatch("disputeReason", form);
    const dispatch = useDispatch();
    const {
        disputeTypes,
        disputePriority,
        disputeReason,
        disputeUsers,
        disputeSubmitLoading,
        error,
    } = useSelector((state) => state.payments);

    useEffect(() => {
        dispatch(getAllDisputePriority());
        dispatch(getAllDisputeType());
        dispatch(getDisputeRaisedFor(data?.transactionId));
    }, [dispatch, data?.transactionId]);

    const onDisputeTypeChange = (value) => {
        form.setFieldsValue({ disputeReason: undefined });
        dispatch(getAllDisputeReasonById(value));
    }

    const onDisputeDateChange = (value) => {
        const age = disputeAgeCalculation(value)
        form.setFieldsValue({ disputeAge: `${age} day(s)` });
    }

    const handleSubmit = async (values) => {
        const payload = {
            transactionId: data?.transactionId,
            disputeDate: values.disputeDate,
            disputeAmount: String(values.disputeAmount),
            disputeTypeId: values.disputeType,
            disputeReasonId: values.disputeReason,
            disputeRaisedFor: values.disputeRaisedFor,
            disputePriorityId: values.disputePriority,
            disputeReasonDesc: values.disputeReasonDesc,
            disputeComments: values.comments,
        };

        if (values.disputeReason !== "DRS1000004") {
            delete payload.disputeReasonDesc;
        }

        const res = await dispatch(addDispute(payload));

        if (res.payload.statusCode === 201) {
            onClose();
            dispatch(getTxnDetails(data?.transactionId));
        }
    }

    return (
        <div className="raise-dispute-form-container">
            <div className="txn-details-container">
                <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                        <Flex gap={15} vertical >
                            <DealDetail
                                label="Transaction ID"
                                value={data?.transactionId || GLOBAL_CONSTANTS.NA}
                            />
                            <DealDetail
                                label="UTR"
                                value={data?.utr || GLOBAL_CONSTANTS.NA}
                            />
                            <DealDetail
                                label="Particulars"
                                value={data?.particulars || GLOBAL_CONSTANTS.NA}
                            />
                        </Flex>
                    </Col>
                    <Col xs={24} md={12}>
                        <Flex gap={15} vertical>
                            <DealDetail
                                label="Transaction Status"
                                value={<Text className='txn-status'>{capitalizeWords(data?.transactionStatus)}</Text> || GLOBAL_CONSTANTS.NA}
                            />
                            <DealDetail
                                label="Transaction Date"
                                value={formatPaymentDateTime(data?.transactionDate) || GLOBAL_CONSTANTS.NA}
                            />
                            <DealDetail
                                label="Transaction Amount"
                                value={formatCurrency(data?.transactionAmount) || GLOBAL_CONSTANTS.NA}
                            />
                        </Flex>
                    </Col>
                </Row>
            </div>
            <Form
                layout="vertical"
                form={form}
                onFinish={handleSubmit}
                initialValues={{ remember: true, disputeAge: 0 }}
            >
                <Row gutter={[24, 0]}>
                    <Col xs={24} md={12}>
                        <CustomDatePicker
                            label="Dispute Date"
                            name="disputeDate"
                            isRequired={true}
                            placeholder="Select Dispute Date"
                            onChange={(date) => onDisputeDateChange(date)}
                            rules={[{ required: true, message: ERROR_MSGS.DIS_DATE_ERR_MSG }]}
                            disabledDate={(current) => {
                                if (!current) return false;
                                const txnDate = dayjs(data?.transactionDate).startOf("day");
                                const today = dayjs().endOf("day");

                                return current.isBefore(txnDate, "day") || current.isAfter(today, "day");
                            }}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <CustomSelect
                            label="Dispute Type"
                            name="disputeType"
                            showSearch
                            isRequired={true}
                            placeholder="Select Dispute Type"
                            options={disputeTypes?.map((type) => ({
                                value: type.disputeTypeId,
                                label: capitalizeWords(type.disputeTypeName),
                            }))}
                            onChange={(value) => onDisputeTypeChange(value)}
                            rules={[{ required: true, message: ERROR_MSGS.SEL_DIS_TYPE_ERR_MEG }]}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <CustomInput
                            label="Dispute Age"
                            name="disputeAge"
                            type="text"
                            isRequired={true}
                            disabled
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <CustomInputNumber
                            label="Dispute Amount"
                            name="disputeAmount"
                            isRequired={true}
                            controls={false}
                            placeholder="Enter Dispute Amount"
                            rules={[
                                { required: true, message: ERROR_MSGS.DIS_AMT_ERR_MSG },
                                {
                                    validator(_, value) {
                                        const disAmt = parseInt(value);
                                        if (disAmt > data?.transactionAmount) {
                                            return Promise.reject(new Error(`Dispute amount cannot exceed transaction amount (${data?.transactionAmount})`));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <CustomSelect
                            label="Dispute Priority"
                            name="disputePriority"
                            showSearch
                            isRequired={true}
                            placeholder="Select Dispute Priority"
                            options={disputePriority?.map((type) => ({
                                value: type.disputePriorityId,
                                label: capitalizeWords(type.disputePriorityDesc),
                            }))}
                            rules={[{ required: true, message: ERROR_MSGS.SEL_DIS_PRI_ERR_MSG }]}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <CustomSelect
                            label="Dispute Reason"
                            name="disputeReason"
                            showSearch
                            isRequired={true}
                            placeholder="Select Dispute Reason"
                            options={disputeReason?.map((dispute) => ({
                                value: dispute.disputeReasonId,
                                label: capitalizeWords(dispute.disputeReasonDesc),
                            }))}
                            rules={[{ required: true, message: ERROR_MSGS.SEL_DIS_REA_ERR_MSG }]}
                        />
                    </Col>
                    {disputeReasonValue === "DRS1000004" && (
                        <Col xs={24}>
                            <CustomInput
                                label="Enter Reason"
                                name="disputeReasonDesc"
                                type="text"
                                isRequired={true}
                                placeholder="Enter Reason"
                                rules={reasonRules}
                            />
                        </Col>
                    )}
                    <Col xs={24}>
                        <CustomSelect
                            label="Dispute Raised For"
                            name="disputeRaisedFor"
                            isRequired={true}
                            placeholder="Select Dispute Raised For"
                            options={disputeUsers?.map((user) => ({
                                value: user.bankAccId,
                                label: capitalizeWords(user.name),
                            }))}
                            rules={[{ required: true, message: ERROR_MSGS.SEL_DIS_RAISE_FOR_ERR_MSG }]}
                        />
                    </Col>

                    <Col xs={24}>
                        <CustomInput
                            label="Dispute Comments"
                            name="comments"
                            rows={3}
                            isRequired={true}
                            isTextArea={true}
                            type="text"
                            placeholder="Enter your Comments"
                            rules={CommentsRules}
                        />
                    </Col>
                </Row>

                <FormFooter
                    onCancelClick={() => {
                        onClose();
                        form.resetFields();
                    }}
                    submitLoading={disputeSubmitLoading}
                    btnName={BTN_TXT.SUBMIT}
                    error={error}
                />

            </Form>
        </div>
    )
}

export default RaiseDisputeForm